import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { PageHeader } from "@/components/base/PageHeader";
import {
  listingTemplates,
  templateById,
  templateChannelLabel,
} from "@/lib/listing-templates";
import {
  previewDatasets,
  datasetById,
  type PreviewDatasetId,
} from "@/lib/listing-template-datasets";
import TemplateRenderer from "@/pages/listing-templates/components/TemplateRenderer";
import { BrowserFrame, PhoneFrame } from "@/pages/listing-templates/components/PreviewFrame";

type Device = "desktop" | "mobile";

export default function ListingTemplatePreview() {
  const { templateId } = useParams();
  const active = templateById(templateId) ?? listingTemplates[0];
  const [device, setDevice] = useState<Device>("desktop");
  const [datasetId, setDatasetId] = useState<PreviewDatasetId>("rich");
  const dataset = datasetById(datasetId);

  return (
    <div>
      <PageHeader title={`Preview — ${active.name}`} subtitle={active.tagline} backTo="/listing-templates">
        <span className="inline-flex items-center gap-1 text-xs font-medium text-slate-500 bg-slate-50 border border-slate-200 rounded-full px-3 py-1.5">
          <i className="ri-store-2-line" />
          {templateChannelLabel(active.channels)}
        </span>
      </PageHeader>

      {/* Controls */}
      <div className="mb-5 flex flex-col lg:flex-row lg:items-center gap-3">
        <div className="flex items-center gap-1 bg-white border border-slate-200 rounded-lg p-1 flex-wrap">
          {listingTemplates.map((t) => (
            <Link
              key={t.id}
              to={`/listing-templates/preview/${t.id}`}
              className={`px-3 py-1.5 text-sm font-medium rounded-md whitespace-nowrap ${
                active.id === t.id ? "bg-emerald-50 text-emerald-700" : "text-slate-600 hover:bg-slate-50"
              }`}
            >
              {t.name}
            </Link>
          ))}
        </div>

        <div className="flex flex-col sm:flex-row sm:items-center gap-2 lg:ml-auto">
          <div className="flex items-center gap-1 bg-white border border-slate-200 rounded-full px-1 py-1">
            {previewDatasets.map((d) => (
              <button
                key={d.id}
                type="button"
                onClick={() => setDatasetId(d.id)}
                className={`px-3 py-1.5 text-sm font-medium rounded-full whitespace-nowrap ${
                  datasetId === d.id ? "bg-slate-800 text-white" : "text-slate-600 hover:bg-slate-50"
                }`}
              >
                {d.name}
              </button>
            ))}
          </div>

          <div className="flex items-center gap-1 bg-white border border-slate-200 rounded-lg p-1">
            <button
              onClick={() => setDevice("desktop")}
              className={`px-3 py-1.5 text-sm font-medium rounded-md flex items-center gap-1.5 whitespace-nowrap ${
                device === "desktop" ? "bg-slate-800 text-white" : "text-slate-600 hover:bg-slate-50"
              }`}
            >
              <i className="ri-computer-line" /> Desktop
            </button>
            <button
              onClick={() => setDevice("mobile")}
              className={`px-3 py-1.5 text-sm font-medium rounded-md flex items-center gap-1.5 whitespace-nowrap ${
                device === "mobile" ? "bg-slate-800 text-white" : "text-slate-600 hover:bg-slate-50"
              }`}
            >
              <i className="ri-smartphone-line" /> Mobile
            </button>
          </div>
        </div>
      </div>

      <p className="text-xs text-slate-400 mb-4">
        {dataset.description} Shown through this template, approximately as a marketplace customer would see it.
      </p>

      {/* Preview frame */}
      <div className="rounded-xl bg-slate-100 border border-slate-200 p-4 sm:p-6">
        {device === "desktop" ? (
          <div className="mx-auto max-w-5xl">
            <BrowserFrame>
              <TemplateRenderer templateId={active.id} product={dataset.product} />
            </BrowserFrame>
          </div>
        ) : (
          <div className="flex justify-center">
            <PhoneFrame>
              <TemplateRenderer templateId={active.id} product={dataset.product} />
            </PhoneFrame>
          </div>
        )}
      </div>

      {/* Description */}
      <div className="mt-6 bg-white rounded-xl border border-slate-200 p-5">
        <h3 className="text-sm font-semibold text-slate-800 mb-1.5">About this template</h3>
        <p className="text-sm text-slate-600 leading-relaxed">{active.description}</p>
        <div className="flex flex-wrap gap-1.5 mt-3">
          {active.suitability.map((tag) => (
            <span key={tag} className="text-xs text-slate-500 bg-slate-50 rounded-full px-2 py-0.5">
              {tag}
            </span>
          ))}
        </div>
        <div className="mt-4 pt-4 border-t border-slate-100">
          <Link
            to="/listing-templates/review"
            className="inline-flex items-center gap-1.5 text-sm font-medium text-emerald-700 hover:text-emerald-800"
          >
            <i className="ri-layout-grid-line" /> Compare all templates
          </Link>
        </div>
      </div>
    </div>
  );
}